import { useMutation, useQueryClient } from '@tanstack/react-query';

import type {
  ApplePayDomainRequest,
  ConnectStripeDirectKeysRequest,
  StripeConnectCallbackRequest,
} from '@nucleus-commerce/api-client/admin';

import { leaveApp } from '@/shared/browser/leave-app';

import { stripeApi } from '../api/stripe-api';

import { stripeKeys } from './stripe-keys';

/**
 * Asks for the Connect URL and leaves for Stripe. 409 PAYMENT_PROVIDER_UNAVAILABLE means the
 * platform has no Connect client id; the card explains and offers the API-key form instead.
 */
export function useStartStripeConnect(storeId: string) {
  return useMutation({
    mutationFn: () => stripeApi.connect(storeId),
    meta: { silentError: true },
    onSuccess: ({ url }) => leaveApp(url),
  });
}

export function useCompleteStripeConnect(storeId: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (body: StripeConnectCallbackRequest) => stripeApi.completeConnect(storeId, body),
    meta: { successMessage: 'paymentMethods:stripe.toasts.connected', silentError: true },
    onSuccess: (account) => queryClient.setQueryData(stripeKeys.account(storeId), account),
  });
}

/** 422 PAYMENT_PROVIDER_REJECTED when Stripe refuses the secret key. */
export function useConnectStripeWithKeys(
  storeId: string,
  successMessage = 'paymentMethods:stripe.toasts.connected',
) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (body: ConnectStripeDirectKeysRequest) => stripeApi.connectWithKeys(storeId, body),
    meta: { successMessage, silentError: true },
    onSuccess: (account) => queryClient.setQueryData(stripeKeys.account(storeId), account),
  });
}

export function useAddApplePayDomain(storeId: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (body: ApplePayDomainRequest) => stripeApi.addApplePayDomain(storeId, body),
    meta: { successMessage: 'paymentMethods:stripe.applePay.toasts.added', silentError: true },
    onSuccess: (account) => {
      queryClient.setQueryData(stripeKeys.account(storeId), account);
      return queryClient.invalidateQueries({ queryKey: stripeKeys.applePayDomains(storeId) });
    },
  });
}

export function useRemoveApplePayDomain(storeId: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (domain: string) => stripeApi.removeApplePayDomain(storeId, domain),
    meta: { successMessage: 'paymentMethods:stripe.applePay.toasts.removed' },
    onSuccess: () =>
      Promise.all([
        queryClient.invalidateQueries({ queryKey: stripeKeys.applePayDomains(storeId) }),
        queryClient.invalidateQueries({ queryKey: stripeKeys.account(storeId), exact: true }),
      ]),
  });
}

/** Stores (or replaces) the direct-keys signing secret; the response says `webhookSecretSet`. */
export function useSetWebhookSecret(storeId: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (secret: string) => stripeApi.setWebhookSecret(storeId, secret),
    meta: { successMessage: 'paymentMethods:stripe.webhookSecret.saved', silentError: true },
    onSuccess: (account) => queryClient.setQueryData(stripeKeys.account(storeId), account),
  });
}

export function useDisconnectStripe(storeId: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: () => stripeApi.disconnect(storeId),
    meta: { successMessage: 'paymentMethods:stripe.toasts.disconnected', silentError: true },
    onSuccess: () => queryClient.invalidateQueries({ queryKey: stripeKeys.account(storeId) }),
  });
}
